import React, { createContext, useContext } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

// Define strict component interfaces with proper typing
export interface DropdownComponents {
  Dropdown: React.ComponentType<React.ComponentProps<typeof Menu>>;
  DropdownButton: React.ComponentType<React.ComponentProps<typeof MenuButton>>;
  DropdownMenu: React.ComponentType<React.ComponentProps<typeof MenuItems>>;
  DropdownItem: React.ComponentType<React.ComponentProps<typeof MenuItem>>;
}

// Default components using HeadlessUI
const defaultComponents: DropdownComponents = {
  Dropdown: Menu,
  DropdownButton: MenuButton,
  DropdownMenu: MenuItems,
  DropdownItem: MenuItem,
};

// Create the context with better error handling
const ComponentContext = createContext<DropdownComponents | null>(null);

// Provider component with better TypeScript
export interface NotebookComponentProviderProps {
  children: React.ReactNode;
  components?: Partial<DropdownComponents>;
}

export function NotebookComponentProvider({
  children,
  components = {},
}: NotebookComponentProviderProps) {
  const mergedComponents: DropdownComponents = {
    ...defaultComponents,
    ...components,
  };

  return (
    <ComponentContext.Provider value={mergedComponents}>
      {children}
    </ComponentContext.Provider>
  );
}

// Hook with better error messaging
export function useNotebookComponents(): DropdownComponents {
  const context = useContext(ComponentContext);
  if (!context) {
    // Fallback to defaults if no provider found
    console.warn(
      'useNotebookComponents: No NotebookComponentProvider found, using default components',
    );
    return defaultComponents;
  }
  return context;
}

// Typed individual hooks
export function useDropdown(): DropdownComponents['Dropdown'] {
  return useNotebookComponents().Dropdown;
}

export function useDropdownButton(): DropdownComponents['DropdownButton'] {
  return useNotebookComponents().DropdownButton;
}

export function useDropdownMenu(): DropdownComponents['DropdownMenu'] {
  return useNotebookComponents().DropdownMenu;
}

export function useDropdownItem(): DropdownComponents['DropdownItem'] {
  return useNotebookComponents().DropdownItem;
}
